// umlaut-check: skip — Variablen-/Funktionsnamen ("uebernehmen", "laedt"
// usw.) sind technische Bezeichner; sichtbare Texte tragen Umlaute korrekt.

import React, {useRef, useState} from "react";
import {Modal, OverlayTrigger, Tooltip} from "react-bootstrap";
import axios from "axios";
import {PiCheckCircle, PiUploadSimple, PiWarningCircle} from "react-icons/pi";
import HelpTooltip from "../HelpTooltip.jsx";
import {formatDatum, formatMenge} from "./format.js";

const KACHEL_HILFE = {
    "Lieferungen neu": "Anzahl der Lieferungen (LKW), die durch diesen Import neu entstehen.",
    "Lieferungen betroffen": "Anzahl der bereits bestehenden Lieferungen, an denen dieser Import etwas ändert.",
    "Positionen neu": "Anzahl der Farbtöne, die es in den betroffenen Lieferungen bisher noch nicht gab.",
    "Positionen geändert": "Anzahl der bestehenden Positionen, bei denen sich mindestens ein Wert (Menge, Termin, Ampel …) ändert.",
    "Positionen unverändert": "Anzahl der Positionen in der Datei, die exakt dem bereits gespeicherten Stand entsprechen und deshalb nichts auslösen.",
    "Produktionsstände": "Anzahl der Produktionsstand-Meldungen (Block PRODUCTION) in dieser Datei.",
    Verfügbarkeiten: "Anzahl der Lagerverfügbarkeits-Zeilen (Blätter „CW …“) in dieser Datei.",
    Verhandlungszeilen: "Anzahl der Verhandlungs-Einträge (Blatt „MOLOTOW (2)“: ursprünglich/nachgebessert/final) in dieser Datei.",
    "Artikel gesamt": "Anzahl der Artikel, die dieser Bestellrechner insgesamt aufführt.",
    "Artikel neu": "Anzahl der Artikel, die noch keinen Parametersatz hatten und dadurch neu angelegt werden.",
    "Parameterwerte übernommen": "Anzahl der Werte (Mindestbestand, Zielbestand …), die aus der Datei übernommen werden.",
    "Übersprungen (von Hand gepflegt)": "Anzahl der Werte, die NICHT übernommen werden, weil sie bereits von Hand gepflegt sind - von Hand geht immer vor Datei.",
    Bewegungszeilen: "Anzahl der Tageszeilen (Artikel und Tag) in der SNV-Datei, die übernommen werden.",
    "Artikel (SNV)": "Anzahl verschiedener Artikel in der SNV-Datei, nach dem Abschneiden der Präfixe MO, MOCO, MOS und MOSFMAG.",
    "Zeilen unklar": "Zeilen, die NICHT übernommen werden - etwa weil Pakete + Paletten + Schneider nicht die Summe „abgang_alle“ ergeben oder das Datum unlesbar ist. Beispiele stehen unten.",
    "Abgang ab SNV Weilheim": "Summe aus abgang_weclapp (Pakete) und abgang_paletten über den ganzen Zeitraum - das ist die Feuerstein-Ware, die ab Weilheim verkauft wurde.",
    "Abgang Schneider": "Summe abgang_schneider - Schneiders eigene Verkäufe. Zählt für die Lager-Aufteilung nicht mit.",
    Zugang: "Summe aller Zugänge im Lager SNV Weilheim über den ganzen Zeitraum der Datei.",
    "Zeilen in der Liste": "Anzahl der Artikelzeilen in der Cosmos-Liste (beide Blätter FLAME und MOLOTOW, ohne Kategorie- und Summenzeilen). Jede Zeile bleibt erhalten, auch doppelte.",
    "Dosen gesamt": "Summe der Spalte STOCK über alle Zeilen – brutto: offene Bestellungen sind enthalten und von Cosmos nicht getrennt ausgewiesen. Entspricht der Summe, die Cosmos selbst unten im Blatt nennt.",
    "Mit Weclapp-Nummer": "Zeilen, deren Cosmos-Code sicher einer Weclapp-Artikelnummer zugeordnet ist (PREMIUM und BURNER nach Regel, FLAME aus der Zuordnungstabelle).",
    "Nicht zugeordnet": "Zeilen, für die keine Weclapp-Artikelnummer bekannt ist. Sie werden trotzdem übernommen und zählen in der Summe mit - unten stehen sie einzeln.",
    "Doppelte Codes (Zeilen)": "Zeilen, deren Cosmos-Code mehrfach in der Liste steht. Sie werden NICHT addiert und nicht verworfen, sondern einzeln übernommen und als Dublette gemeldet.",
    Klärungsfälle: "Codes, deren Zuordnung nicht geraten werden darf (z. B. 0410901: in der Cosmos-Revision doppelt vergeben). Menge wird übernommen, die Weclapp-Nummer bleibt offen.",
};

const DATEITYP_NAME = {ampel: "Ampel-Datei", bestellrechner: "Bestellrechner", snv_bewegungen: "SNV-Warenbewegung", bestandsliste: "Cosmos-Bestandsliste"};

const ZUORDNUNG_LABEL = {nicht_zugeordnet: "nicht zugeordnet", dublette: "Dublette", klaerung: "Klärungsfall"};
const ZUORDNUNG_BADGE = {nicht_zugeordnet: "secondary", dublette: "warning", klaerung: "danger"};
const ZUORDNUNG_HILFE = {
    nicht_zugeordnet: "Für diesen Cosmos-Code ist keine Weclapp-Artikelnummer bekannt. Die Menge zählt in der Summe mit.",
    dublette: "Dieser Code steht mehrfach in der Liste. Die Mengen wurden nicht addiert - jede Zeile steht einzeln da.",
    klaerung: "Die Zuordnung dieses Codes darf nicht geraten werden und ist mit Cosmos zu klären.",
};

const FARB_LABEL = {gruen: "Grün (Zusage deckt Wunsch)", gelb: "Gelb (Teilmenge)", rot: "Rot (verschoben)", rosa: "Rosa (neuer Vorschlag)", ohne: "Ohne Ampel"};
const FARB_BADGE = {gruen: "success", gelb: "warning", rot: "danger", rosa: "info", ohne: "secondary"};

/** "TT.MM.JJJJ HH:MM" ohne das Komma, das "toLocaleString('de-DE', …)" einfügt (Sichtprüfung 1, Befund 24). */
function formatZeitpunkt(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return String(iso);
    }
    const zweistellig = (n) => String(n).padStart(2, "0");
    return `${zweistellig(d.getDate())}.${zweistellig(d.getMonth() + 1)}.${d.getFullYear()} ${zweistellig(d.getHours())}:${zweistellig(d.getMinutes())}`;
}

/**
 * Zweistufiger Datei-Import der Ampel-Datei, von Oskars Bestellrechner und
 * der SNV-Warenbewegung (Auftrag #51, 28.09.2026)
 * (AP6, PLAN.md Abschnitt 6): Datei wählen und prüfen lassen ("analysieren",
 * schreibt noch nichts), Vorschau ansehen, erst dann "Übernehmen" schreibt
 * tatsächlich. Kein sichtbares natives Dateifeld (KONVENTIONEN.md
 * Abschnitt 4) - eine eigene Ablagefläche löst den versteckten
 * `<input type="file">` per Klick aus.
 */
export default function DateiImportModal({show, onClose, onUebernommen}) {
    const [schritt, setSchritt] = useState(1);
    const [datei, setDatei] = useState(null);
    const [laedt, setLaedt] = useState(false);
    const [fehler, setFehler] = useState(null);
    const [vorschau, setVorschau] = useState(null);
    const [importId, setImportId] = useState(null);
    const [uebernommen, setUebernommen] = useState(false);
    const dateiFeldRef = useRef(null);

    const zuruecksetzen = () => {
        setSchritt(1);
        setDatei(null);
        setFehler(null);
        setVorschau(null);
        setImportId(null);
        setUebernommen(false);
    };

    /**
     * N3 (Rückmeldung 22.09.2026): "Abbrechen" verwirft eine bereits
     * geprüfte, aber noch nicht übernommene Vorschau SOFORT serverseitig -
     * statt sie liegen zu lassen, bis die nächste "Analysieren"-Vorschau
     * desselben Benutzers oder die 24-Stunden-Frist sie aufräumt
     * (VorschauAufraeumer). Kein Effekt, wenn schon übernommen wurde.
     */
    const schliessen = () => {
        if (importId && !uebernommen) {
            axios.post(`/cosmos/daten/importe/${importId}/verwerfen`).catch(() => {});
        }
        onClose();
        zuruecksetzen();
    };

    const dateiWaehlen = (gewaehlteDatei) => {
        setDatei(gewaehlteDatei || null);
        setFehler(null);
    };

    const aufAblegen = (event) => {
        event.preventDefault();
        const gewaehlteDatei = event.dataTransfer?.files?.[0];
        if (gewaehlteDatei) {
            dateiWaehlen(gewaehlteDatei);
        }
    };

    const analysieren = () => {
        if (!datei) {
            setFehler("Bitte zuerst eine Datei auswählen.");
            return;
        }
        setLaedt(true);
        setFehler(null);
        const formular = new FormData();
        formular.append("datei", datei);

        axios
            .post("/cosmos/daten/importe/analysieren", formular)
            .then(({data}) => {
                setVorschau(data);
                setImportId(data.import_id);
                setSchritt(2);
            })
            .catch((e) => {
                setFehler(e.response?.data?.message || "Die Datei konnte nicht geprüft werden.");
            })
            .finally(() => setLaedt(false));
    };

    const uebernehmen = () => {
        if (!importId) {
            return;
        }
        setLaedt(true);
        setFehler(null);
        axios
            .post(`/cosmos/daten/importe/${importId}/uebernehmen`)
            .then(() => {
                setUebernommen(true);
                onUebernommen && onUebernommen();
            })
            .catch((e) => {
                setFehler(e.response?.data?.message || "Die Übernahme ist fehlgeschlagen.");
            })
            .finally(() => setLaedt(false));
    };

    const v = vorschau?.vorschau || {};
    const istAmpel = vorschau?.typ === "ampel";
    const istSnv = vorschau?.typ === "snv_bewegungen";
    const istBestandsliste = vorschau?.typ === "bestandsliste";
    const bestandsMeldungen = v.meldungen || [];
    const bestandsDubletten = v.dubletten || [];
    const ampelJeFarbe = v.ampel_je_farbe || {};
    const unbekannteArtikel = v.unbekannte_artikel || [];
    const beispiele = v.beispiele || [];

    return (
        <Modal show={show} onHide={schliessen} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="h6 d-flex align-items-center gap-2">
                    Datei importieren
                    <HelpTooltip placement="right" size={0.8}>
                        Die Datei wird zuerst nur geprüft – es wird nichts geändert. Erst nach der Bestätigung
                        „Übernehmen“ landen die Zeilen in den Lieferungen, in der SNV-Warenbewegung bzw. im Cosmos-Pufferbestand. Jeder übernommene Import ist danach über
                        den Knopf „Rückgängig“ wieder aufhebbar, solange kein neuerer Import folgt.
                    </HelpTooltip>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {fehler && (
                    <div className="alert alert-danger py-2 small d-flex align-items-start gap-2">
                        <PiWarningCircle className="flex-shrink-0 mt-1" />
                        <span>{fehler}</span>
                    </div>
                )}

                {schritt === 1 && (
                    <div>
                        <p className="small text-muted">
                            Erkannt werden die Cosmos-Bestandsliste „Cosmos Stock Overview“ (Blätter „FLAME“/„MOLOTOW“ mit
                            den Spalten CATEGORY, CODE, DESCRIPTION, STOCK; das Datum steht als JJJJMMTT vorn im
                            Dateinamen), die Cosmos-Ampel-Datei (Blätter „MOLOTOW“/„FLAME“), Oskars
                            Bestellrechner (Blatt „Bestellvorbereitung“) und die SNV-Warenbewegung von Schneider
                            Novus (Spalten „artnr“, „jjjjmmtt“, „abgang_weclapp“, „abgang_paletten“) – anhand des
                            Inhalts, unabhängig vom Dateinamen.
                        </p>
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => dateiFeldRef.current?.click()}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && dateiFeldRef.current?.click()}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={aufAblegen}
                            className="border border-2 border-dashed rounded p-4 text-center bg-body-secondary"
                            style={{cursor: "pointer"}}
                        >
                            <PiUploadSimple size={28} className="mb-2 text-muted" />
                            <div className="fw-semibold">{datei ? datei.name : "Datei hierher ziehen oder klicken zum Auswählen"}</div>
                            <div className="small text-muted mt-1">.xlsx</div>
                        </div>
                        <input
                            ref={dateiFeldRef}
                            type="file"
                            accept=".xlsx"
                            className="d-none"
                            onChange={(e) => dateiWaehlen(e.target.files?.[0])}
                        />
                    </div>
                )}

                {schritt === 2 && (
                    <div>
                        <div className="d-flex flex-wrap gap-2 mb-2">
                            <OverlayTrigger placement="top" overlay={<Tooltip id="import-dateityp-tip">Anhand des Inhalts erkannter Dateityp - unabhängig vom Dateinamen.</Tooltip>}>
                                <span className="badge text-bg-secondary">Dateityp: {DATEITYP_NAME[vorschau?.typ] || vorschau?.typ}</span>
                            </OverlayTrigger>
                            {istSnv && v.zeitraum_von && (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="import-zeitraum-tip">
                                            Zeitraum, den die Datei abdeckt ({v.zeitraum_quelle === "dateiname" ? "aus dem Dateinamen" : "erster bis letzter Tag mit Daten"}). Für die Lager-Aufteilung zählen nur Monate, die vollständig darin liegen.
                                        </Tooltip>
                                    }
                                >
                                    <span className="badge text-bg-secondary">
                                        Zeitraum: {formatDatum(v.zeitraum_von)} – {formatDatum(v.zeitraum_bis)}
                                    </span>
                                </OverlayTrigger>
                            )}
                            {vorschau?.stand_am && !istSnv && (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="import-stand-tip">
                                            {istBestandsliste
                                                ? "Stand der Cosmos-Liste - das Datum JJJJMMTT aus dem Dateinamen. Jede Liste bleibt als eigene Momentaufnahme erhalten."
                                                : "Datum, auf das sich diese Datei bezieht (aus den Excel-Dokumenteigenschaften, nicht aus dem Dateinamen)."}
                                        </Tooltip>
                                    }
                                >
                                    {/* #24-Rest (Sichtprüfung 2, 22.09.2026): TT.MM.JJJJ statt roher ISO-Zeichenkette. */}
                                    <span className="badge text-bg-secondary">Stand: {formatDatum(vorschau.stand_am)}</span>
                                </OverlayTrigger>
                            )}
                        </div>

                        {istBestandsliste && vorschau?.stand_am_sicher === false && (
                            <div className="alert alert-warning py-2 small">
                                Im Dateinamen steht kein Datum im Format JJJJMMTT (z. B. „20261002 OCTOBER FLAME & MOLOTOW STOCK.xlsx“).
                                Als Stand wird ersatzweise der {formatDatum(vorschau.stand_am)} angenommen - bitte die Datei
                                umbenennen und neu prüfen lassen, wenn das nicht stimmt.
                            </div>
                        )}

                        {istBestandsliste && v.gleicher_stand_vorhanden && (
                            <div className="alert alert-warning py-2 small">
                                Für den Stand {formatDatum(vorschau.stand_am)} ist bereits eine Liste übernommen. Diese käme als weitere
                                Momentaufnahme dazu; angezeigt wird die zuletzt übernommene.
                            </div>
                        )}

                        {istBestandsliste && (
                            <div className="alert alert-info py-2 small">
                                Die Mengen sind <strong>brutto</strong>: offene Bestellungen sind enthalten und nicht getrennt ausgewiesen.
                                Der Bestand ist nur ein Anhaltspunkt und geht nicht in die Prognose ein.
                                {v.vorheriger_stand_am && (
                                    <>
                                        {" "}
                                        Vorheriger Stand {formatDatum(v.vorheriger_stand_am)}: {formatMenge(v.vorherige_summe)} Dosen.
                                    </>
                                )}
                            </div>
                        )}

                        {vorschau?.bereits_importiert_am && (
                            <div className="alert alert-warning py-2 small">
                                Diese Datei (identischer Inhalt) wurde bereits am{" "}
                                {formatZeitpunkt(vorschau.bereits_importiert_am)} übernommen. Eine
                                erneute Übernahme ist trotzdem möglich.
                            </div>
                        )}

                        {vorschau?.blaetter?.uebersprungen?.length > 0 && (
                            <div className="small text-muted mb-2">
                                Erkannt, aber übersprungen (umsortierte Arbeitskopien oder unbekannte Blätter):{" "}
                                {vorschau.blaetter.uebersprungen.join(", ")}
                            </div>
                        )}

                        {istBestandsliste ? (
                            <div className="row row-cols-2 row-cols-md-3 g-2 mb-3">
                                <VorschauKachel label="Zeilen in der Liste" wert={v.zeilen_gesamt} />
                                <VorschauKachel label="Dosen gesamt" wert={v.summe} />
                                <VorschauKachel label="Mit Weclapp-Nummer" wert={v.zugeordnet} />
                                <VorschauKachel label="Nicht zugeordnet" wert={v.nicht_zugeordnet} />
                                <VorschauKachel label="Doppelte Codes (Zeilen)" wert={v.dubletten_zeilen} />
                                <VorschauKachel label="Klärungsfälle" wert={v.klaerung} />
                                <VorschauKachel label="Zeilen unklar" wert={v.zeilen_unklar} />
                            </div>
                        ) : istSnv ? (
                            <div className="row row-cols-2 row-cols-md-3 g-2 mb-3">
                                <VorschauKachel label="Bewegungszeilen" wert={v.zeilen_gueltig} />
                                <VorschauKachel label="Artikel (SNV)" wert={v.artikel} />
                                <VorschauKachel label="Zeilen unklar" wert={v.zeilen_unklar} />
                                <VorschauKachel label="Abgang ab SNV Weilheim" wert={v.ab_snv_weilheim} />
                                <VorschauKachel label="Abgang Schneider" wert={v.summen?.abgang_schneider} />
                                <VorschauKachel label="Zugang" wert={v.summen?.zugang} />
                            </div>
                        ) : istAmpel ? (
                            <div className="row row-cols-2 row-cols-md-3 g-2 mb-3">
                                <VorschauKachel label="Lieferungen neu" wert={v.lieferungen_neu} />
                                <VorschauKachel label="Lieferungen betroffen" wert={v.lieferungen_geaendert} />
                                <VorschauKachel label="Positionen neu" wert={v.positionen_neu} />
                                <VorschauKachel label="Positionen geändert" wert={v.positionen_geaendert} />
                                <VorschauKachel label="Positionen unverändert" wert={v.positionen_unveraendert} />
                                <VorschauKachel label="Produktionsstände" wert={v.produktionsstaende_anzahl} />
                                <VorschauKachel label="Verfügbarkeiten" wert={v.verfuegbarkeiten_anzahl} />
                                <VorschauKachel label="Verhandlungszeilen" wert={v.verhandlungszeilen_anzahl} />
                            </div>
                        ) : (
                            <div className="row row-cols-2 row-cols-md-3 g-2 mb-3">
                                <VorschauKachel label="Artikel gesamt" wert={v.artikel_gesamt} />
                                <VorschauKachel label="Artikel neu" wert={v.artikel_neu} />
                                <VorschauKachel label="Parameterwerte übernommen" wert={v.parameterwerte_uebernommen} />
                                <VorschauKachel label="Übersprungen (von Hand gepflegt)" wert={v.parameterwerte_uebersprungen_gepflegt} />
                            </div>
                        )}

                        {istAmpel && Object.values(ampelJeFarbe).some((n) => n > 0) && (
                            <div className="mb-3">
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="import-ampel-tip">
                                            Bezieht sich NUR auf die oben gezählten neuen und geänderten Positionen - die unveränderten
                                            zählen hier nicht mit, auch wenn ihre Summe zufällig ähnlich hoch sein kann.
                                        </Tooltip>
                                    }
                                >
                                    <div className="small fw-semibold mb-1" style={{cursor: "help", borderBottom: "1px dotted #999", display: "inline-block"}}>
                                        Ampel der neuen/geänderten Positionen
                                    </div>
                                </OverlayTrigger>
                                <div className="d-flex flex-wrap gap-2">
                                    {Object.entries(ampelJeFarbe)
                                        .filter(([, n]) => n > 0)
                                        .map(([farbe, n]) => (
                                            <span key={farbe} className={`badge text-bg-${FARB_BADGE[farbe] || "secondary"}`}>
                                                {FARB_LABEL[farbe] || farbe}: {formatMenge(n)}
                                            </span>
                                        ))}
                                </div>
                            </div>
                        )}

                        {unbekannteArtikel.length > 0 && (
                            <div className="alert alert-secondary py-2 small mb-3">
                                <div className="fw-semibold mb-1">Außerhalb des Sortiments ({unbekannteArtikel.length}):</div>
                                <ul className="mb-0 ps-3">
                                    {unbekannteArtikel.slice(0, 10).map((a) => (
                                        <li key={a}>{a}</li>
                                    ))}
                                    {unbekannteArtikel.length > 10 && <li>und {unbekannteArtikel.length - 10} weitere</li>}
                                </ul>
                            </div>
                        )}

                        {istBestandsliste && v.summe_je_marke && (
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {Object.entries(v.summe_je_marke).map(([marke, menge]) => (
                                    <OverlayTrigger
                                        key={marke}
                                        placement="top"
                                        overlay={<Tooltip id={`import-marke-tip-${marke}`}>Summe der Spalte STOCK im Blatt {marke} - brutto, enthält offene Bestellungen.</Tooltip>}
                                    >
                                        <span className="badge text-bg-secondary" style={{cursor: "help"}}>
                                            {marke}: {formatMenge(menge)} Dosen
                                        </span>
                                    </OverlayTrigger>
                                ))}
                            </div>
                        )}

                        {istBestandsliste && v.summen_abweichungen?.length > 0 && (
                            <div className="alert alert-danger py-2 small mb-3">
                                <div className="fw-semibold mb-1">Summe stimmt nicht mit der Summenzeile der Datei überein:</div>
                                <ul className="mb-0 ps-3">
                                    {v.summen_abweichungen.map((a) => (
                                        <li key={a.blatt}>
                                            Blatt {a.blatt}: Zeilen {formatMenge(a.summe_zeilen)}, Datei {formatMenge(a.summe_datei)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {istBestandsliste && bestandsDubletten.length > 0 && (
                            <div className="alert alert-warning py-2 small mb-3">
                                <div className="fw-semibold mb-1">Doppelte Codes – nicht addiert, Klärung mit Cosmos:</div>
                                <ul className="mb-0 ps-3">
                                    {bestandsDubletten.map((d) => (
                                        <li key={d.cosmos_code}>
                                            {d.cosmos_code} {d.beschreibung ? `(${d.beschreibung})` : ""}: Mengen {d.mengen.map((m) => formatMenge(m)).join(" / ")}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {istBestandsliste && bestandsMeldungen.length > 0 && (
                            <div className="mb-3">
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id="import-meldungen-tip">Zeilen ohne sichere Weclapp-Zuordnung. Sie werden mit übernommen und bleiben unter „Quellen und Berechnungslogik“ sichtbar.</Tooltip>}
                                >
                                    <div className="small fw-semibold mb-1" style={{cursor: "help", borderBottom: "1px dotted #999", display: "inline-block"}}>
                                        Zeilen ohne sichere Zuordnung ({bestandsMeldungen.length})
                                    </div>
                                </OverlayTrigger>
                                <div className="table-responsive" style={{maxHeight: 240, overflowY: "auto"}}>
                                    <table className="table table-sm table-striped mb-0">
                                        <thead>
                                            <tr>
                                                <th>Marke</th>
                                                <th>Code</th>
                                                <th>Beschreibung</th>
                                                <th className="text-end">Dosen</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bestandsMeldungen.map((m, i) => (
                                                <tr key={`${m.cosmos_code}-${i}`}>
                                                    <td>{m.marke}</td>
                                                    <td>{m.cosmos_code}</td>
                                                    <td className="small">{m.beschreibung}</td>
                                                    <td className="text-end">{formatMenge(m.menge)}</td>
                                                    <td>
                                                        <OverlayTrigger
                                                            placement="left"
                                                            overlay={<Tooltip id={`import-meldung-tip-${i}`}>{m.hinweis || ZUORDNUNG_HILFE[m.status]}</Tooltip>}
                                                        >
                                                            <span className={`badge text-bg-${ZUORDNUNG_BADGE[m.status] || "secondary"}`} style={{cursor: "help"}}>
                                                                {ZUORDNUNG_LABEL[m.status] || m.status}
                                                            </span>
                                                        </OverlayTrigger>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {(istSnv || istBestandsliste) && v.unklar_beispiele?.length > 0 && (
                            <div className="alert alert-warning py-2 small mb-3">
                                <div className="fw-semibold mb-1">Nicht übernommene Zeilen (Beispiele):</div>
                                <ul className="mb-0 ps-3">
                                    {v.unklar_beispiele.slice(0, 10).map((u) => (
                                        <li key={`${u.blatt || ""}-${u.zeile}`}>
                                            {u.blatt ? `Blatt ${u.blatt}, ` : ""}Zeile {u.zeile}, {u.artnr || u.code || "ohne Artikel"}: {u.grund}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {istSnv && v.stichprobe?.length > 0 && (
                            <div className="mb-2">
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id="import-snv-stichprobe-tip">Die ersten Zeilen der Datei so, wie sie übernommen werden - Artikelnummer schon ohne Präfix.</Tooltip>}
                                >
                                    <div className="small fw-semibold mb-1" style={{cursor: "help", borderBottom: "1px dotted #999", display: "inline-block"}}>
                                        Stichprobe
                                    </div>
                                </OverlayTrigger>
                                <div className="table-responsive" style={{maxHeight: 220, overflowY: "auto"}}>
                                    <table className="table table-sm table-striped mb-0">
                                        <thead>
                                            <tr>
                                                <th>Artikel</th>
                                                <th>Tag</th>
                                                <th className="text-end">Zugang</th>
                                                <th className="text-end">Pakete</th>
                                                <th className="text-end">Paletten</th>
                                                <th className="text-end">Schneider</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {v.stichprobe.map((z) => (
                                                <tr key={`${z.artnr_roh}-${z.datum}`}>
                                                    <td>
                                                        {z.artikel_nummer} <span className="text-muted small">({z.artnr_roh})</span>
                                                    </td>
                                                    <td>{formatDatum(z.datum)}</td>
                                                    <td className="text-end">{formatMenge(z.zugang)}</td>
                                                    <td className="text-end">{formatMenge(z.abgang_weclapp)}</td>
                                                    <td className="text-end">{formatMenge(z.abgang_paletten)}</td>
                                                    <td className="text-end">{formatMenge(z.abgang_schneider)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {beispiele.length > 0 && (
                            <div className="mb-2">
                                <div className="small fw-semibold mb-1">Beispiel-Änderungen</div>
                                <div className="table-responsive" style={{maxHeight: 220, overflowY: "auto"}}>
                                    <table className="table table-sm table-striped mb-0">
                                        <thead>
                                            <tr>
                                                <th>Artikel</th>
                                                <th>Feld</th>
                                                <th>Alt</th>
                                                <th>Neu</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {beispiele.map((b, i) => (
                                                <tr key={i}>
                                                    <td>{b.artikel}</td>
                                                    <td>{b.feld}</td>
                                                    <td className="text-muted">{b.alt ?? "–"}</td>
                                                    <td>{b.neu ?? "–"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {uebernommen && (
                            <div className="alert alert-success py-2 small d-flex align-items-center gap-2">
                                <PiCheckCircle className="flex-shrink-0" />
                                {istBestandsliste
                                    ? "Übernommen – der Cosmos-Pufferbestand in der Lieferfähigkeit zeigt ab sofort diesen Stand."
                                    : istSnv
                                      ? "Übernommen – die Lager-Aufteilung der Verkäufe rechnet ab sofort mit dieser Datei."
                                      : "Übernommen – die Liste der Lieferungen wurde aktualisiert."}
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="import-schliessen-tip">{uebernommen ? "Schließt das Fenster - die Übernahme bleibt bestehen." : "Bricht den Import ab, ohne etwas zu ändern."}</Tooltip>}
                >
                    <span>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={schliessen}>
                            {uebernommen ? "Schließen" : "Abbrechen"}
                        </button>
                    </span>
                </OverlayTrigger>
                {schritt === 1 && (
                    <OverlayTrigger placement="top" overlay={<Tooltip id="import-analysieren-tip">Liest die Datei und zeigt eine Vorschau – schreibt noch nichts.</Tooltip>}>
                        <span>
                            <button type="button" className="btn btn-primary btn-sm" disabled={!datei || laedt} onClick={analysieren}>
                                Prüfen
                            </button>
                        </span>
                    </OverlayTrigger>
                )}
                {schritt === 2 && !uebernommen && (
                    <>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="import-andere-datei-tip">Verwirft diese Vorschau (nichts wurde geschrieben) und geht zurück zur Dateiauswahl.</Tooltip>}>
                            <span>
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={zuruecksetzen} disabled={laedt}>
                                    Andere Datei
                                </button>
                            </span>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="import-uebernehmen-tip">{istBestandsliste ? "Speichert diese Liste als neue Momentaufnahme des Cosmos-Pufferbestands - ältere Stände bleiben erhalten, die Übernahme ist rückgängig zu machen." : "Schreibt die geprüften Änderungen – protokolliert und jederzeit rückgängig zu machen."}</Tooltip>}>
                            <span>
                                <button type="button" className="btn btn-primary btn-sm" disabled={laedt} onClick={uebernehmen}>
                                    Übernehmen
                                </button>
                            </span>
                        </OverlayTrigger>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}

function VorschauKachel({label, wert}) {
    if (!wert) {
        return null;
    }
    const hilfe = KACHEL_HILFE[label];
    const inhalt = (
        <div className="border rounded p-2 h-100 bg-white" style={hilfe ? {cursor: "help"} : undefined}>
            <div className="small text-muted">{label}</div>
            <div className="h5 mb-0">{formatMenge(wert)}</div>
        </div>
    );

    return (
        <div className="col">
            {hilfe ? (
                <OverlayTrigger placement="top" overlay={<Tooltip id={`kachel-tip-${label}`}>{hilfe}</Tooltip>}>
                    {inhalt}
                </OverlayTrigger>
            ) : (
                inhalt
            )}
        </div>
    );
}
